/* eslint-disable max-lines */
import { defineStore } from 'pinia'
import type { Building } from '@/types/accessor/api/emergencyInspectionResult/building/building'
import type { BuildingOverview } from '@/types/accessor/api/emergencyInspectionResult/buildingOverview/buildingOverview'
import type { InspectionItemObject } from '@/types/accessor/api/emergencyInspectionResult/assessmentDetails/common/inspectionItemObject'
import * as Step1Types from '@/types/accessor/api/emergencyInspectionResult/assessmentDetails/step1'
import * as Step2Types from '@/types/accessor/api/emergencyInspectionResult/assessmentDetails/step2'
import type { AssessmentInfo } from '@/types/accessor/api/emergencyInspectionResult/assessmentInfo/assessmentInfo'
import type { EmergencyAssessment } from '@/types/accessor/api/emergencyInspectionResult/emergencyAssessment/emergencyAssessment'
import { CONSTRUCTION_TYPE } from '@/constants/accessor/api/emergencyInspectionResult/assessmentInfo/constructionType'
import { cloneDeep } from 'lodash'
import type {
  AssessmentDetailStep2FallingStateTypes,
  AssessmentDetailStep2NeighboringStateTypes
} from '@/types/accessor/api/emergencyInspectionResult/assessmentDetails/step2'
import type { ConstructionType } from '@/types/accessor/api/emergencyInspectionResult/assessmentInfo/constructionType'
import {
  BUILDING_USAGE,
  CONSTRUCTION_METHOD,
  CONSTRUCTION_METHOD_MIXED,
  WOODEN_FLOORS
} from '@/constants/accessor/api/emergencyInspectionResult/buildingOverview'
import { EXTERIOR_RISK_TYPE } from '@/constants/accessor/api/emergencyInspectionResult/assessmentDetails/step1/exteriorRiskType'
import { v4 as uuidV4 } from 'uuid'
import { RISK_RANK } from '@/constants/accessor/api/emergencyInspectionResult/assessmentDetails/common/riskRank'
import { INSPECTION_METHODS } from '@/constants/accessor/api/emergencyInspectionResult/assessmentDetails/step1/inspectionMethod'
import { accessDataBasedOnNetwork } from '@/accessors/common/dataAccessorBasedOnNetwork'
import { useNetworkStore } from './networkStore'
import { buildInspectionResultUpsertParam } from './helpers/inspectionResultUpsertStore'
import { GlobalError } from '@/utils/globalError/GlobalError'
import { ERROR_CODES } from '@/constants/error/errorCode'
import type { ResultSummary } from '@/types/accessor/api/emergencyInspectionResult/emergencyInspectionResult'
import { get as inspectionResultGet } from '@/accessors/indexedDB/disasterEmergencyInspectionResultIdb'
import type { AssessmentDetailMap } from '@/types/accessor/api/emergencyInspectionResult/assessmentDetails/assessmentDetailObject'

/** 調査結果概要の共通部分 */
interface CommonResultSummary {
  assessmentId: string
  insertId: string
  insertDate: string
  updateId: string
  updateDate: string
}

interface InspectionImageData {
  imageDirectory: string
  imageFilePathList: string[]
}

const commonResultSummary: CommonResultSummary = {
  assessmentId: '',
  insertId: '',
  insertDate: '',
  updateId: '',
  updateDate: ''
}

const inspectionItemObject: InspectionItemObject = {
  riskRank: RISK_RANK.UNSELECTED,
  imageDirectory: '',
  imageFilePathList: []
}

const slopeItemObject: Step2Types.SlopeItemObject = {
  riskRank: RISK_RANK.UNSELECTED,
  aboveSlopeFloorLevel: '',
  imageDirectory: '',
  imageFilePathList: []
}

const pillarInspectionObject: Step2Types.PillarItemObject = {
  damagedPillarCount: '',
  inspectedPillarCount: '',
  pillarRatio: '',
  riskRank: RISK_RANK.UNSELECTED,
  imageDirectory: '',
  imageFilePathList: []
}

const assessmentInfo: AssessmentInfo = {
  serialNumber: '',
  inspectedDatetime: null,
  inspectionCount: '',
  inspectorList: ['', '', '', ''],
  constructionType: CONSTRUCTION_TYPE.WOOD
}

const buildingOverview: BuildingOverview = {
  buildingNumber: '',
  buildingName: '',
  buildingAddress: '',
  buildingUsage: BUILDING_USAGE.UNSELECTED,
  buildingUsageOther: '',
  constructionMethod: CONSTRUCTION_METHOD.WOOD.UNSELECTED,
  constructionMethodOther: '',
  constructionMethodMixed: [
    CONSTRUCTION_METHOD_MIXED.UNSELECTED,
    CONSTRUCTION_METHOD_MIXED.UNSELECTED
  ],
  residentialMapSerialNumber: '',
  floorCountWood: WOODEN_FLOORS.UNSELECTED,
  floorCountWoodOther: '',
  floorCountNonWoodAbove: '',
  floorCountNonWoodBasement: '',
  buildingDimensionX: '',
  buildingDimensionY: ''
}

const building: Building = {
  buildingId: undefined,
  location: undefined,
  quadkey: undefined,
  shapeType: undefined,
  shapeCoordinates: undefined
}

const step1: Step1Types.AssessmentDetailStep1 = {
  exterior: {
    exteriorRiskType: EXTERIOR_RISK_TYPE.UNSELECTED,
    exteriorRiskOther: '',
    imageDirectory: '',
    imageFilePathList: []
  },
  inspectionMethod: INSPECTION_METHODS.EXTERNAL
}

const step2NeighboringWood: Step2Types.AssessmentDetailWoodStep2Neighboring = {
  surrounding: inspectionItemObject,
  differentialSettlement: inspectionItemObject,
  basics: inspectionItemObject,
  firstFloorSlope: inspectionItemObject,
  wall: inspectionItemObject,
  corrosionOrTermite: inspectionItemObject
}

const step2NeighboringSteel: Step2Types.AssessmentDetailSteelStep2Neighboring = {
  surrounding: inspectionItemObject,
  settlementSlopeSteel: inspectionItemObject,
  slope: slopeItemObject,
  mostDamagedFloor: '',
  buckling: inspectionItemObject,
  brace: inspectionItemObject,
  pillarBeamJoint: inspectionItemObject,
  pillarBase: inspectionItemObject,
  corrosion: inspectionItemObject
}

const step2NeighboringRc: Step2Types.AssessmentDetailRcStep2Neighboring = {
  partsDamageGte3: inspectionItemObject,
  surrounding: inspectionItemObject,
  groundDestructionSettlement: inspectionItemObject,
  settlementSlope: inspectionItemObject,
  mostDamagedFloor: '',
  pillarDamage5: pillarInspectionObject,
  pillarDamage4: pillarInspectionObject
}

const step2FallingWood: Step2Types.AssessmentDetailWoodStep2Falling = {
  roofTile: inspectionItemObject,
  windowFrame: inspectionItemObject,
  exteriorWetMaterial: inspectionItemObject,
  exteriorDryMaterial: inspectionItemObject,
  signboard: inspectionItemObject,
  outdoorStairs: inspectionItemObject,
  otherItem: inspectionItemObject,
  otherItemName: ''
}

const step2FallingSteel: Step2Types.AssessmentDetailSteelStep2Falling = {
  roofMaterial: inspectionItemObject,
  windowFrame: inspectionItemObject,
  exteriorWetMaterial: inspectionItemObject,
  exteriorDryMaterial: inspectionItemObject,
  signboard: inspectionItemObject,
  outdoorStairs: inspectionItemObject,
  otherItem: inspectionItemObject,
  otherItemName: ''
}

const step2FallingRc: Step2Types.AssessmentDetailRcStep2Falling = {
  windowFrame: inspectionItemObject,
  exteriorWetMaterial: inspectionItemObject,
  exteriorDryMaterial: inspectionItemObject,
  signboard: inspectionItemObject,
  outdoorStairs: inspectionItemObject,
  otherItem: inspectionItemObject,
  otherItemName: ''
}

const emergencyAssessment: EmergencyAssessment = {
  overallResult: null,
  imageDirectory: '',
  imageFilePathList: [],
  overallResultComment: ''
}

const lastFetchedImageData: InspectionImageData[] = []

const defaultData = {
  commonResultSummary,
  assessmentInfo,
  buildingOverview,
  building,
  step1,
  step2NeighboringWood,
  step2NeighboringSteel,
  step2NeighboringRc,
  step2FallingWood,
  step2FallingSteel,
  step2FallingRc,
  emergencyAssessment,
  lastFetchedImageData
}

/** 応急危険度判定用store */
export const useInspectionResultStore = defineStore('inspectionResult', {
  state: () => cloneDeep(defaultData),
  getters: {},
  actions: {
    /** 調査ID発行 */
    setAssessmentId(): void {
      this.commonResultSummary.assessmentId = uuidV4()
    },

    /** 新規建物IDを発行し、建物情報をセットする */
    setNewBuilding(building: Building): void {
      const newBuilding = { ...building, buildingId: uuidV4() }
      this.building = cloneDeep(newBuilding)
    },

    /** 調査結果概要の共通部分更新 */
    setCommonResultSummary(commonResultSummaryParam: CommonResultSummary): void {
      this.commonResultSummary = commonResultSummaryParam
    },

    /** 調査情報更新 */
    setAssessmentInfo(
      serialNumber: string,
      inspectedDatetime: string | null,
      inspectionCount: string,
      inspectorList: string[]
    ): void {
      this.assessmentInfo.serialNumber = serialNumber
      this.assessmentInfo.inspectedDatetime = inspectedDatetime
      this.assessmentInfo.inspectionCount = inspectionCount
      this.assessmentInfo.inspectorList = inspectorList
    },

    /** 建物構造更新 */
    setConstructionType(constructionType: ConstructionType): void {
      this.assessmentInfo.constructionType = constructionType
    },

    /** 家屋位置更新 */
    setBuilding(building: Building) {
      this.building = cloneDeep(building)
    },

    /** 建築物概要情報更新 */
    setBuildingOverview(buildingOverview: BuildingOverview) {
      this.buildingOverview = cloneDeep(buildingOverview)
    },

    /** 調査情報(step1)更新 */
    setStep1(step1: Step1Types.AssessmentDetailStep1) {
      this.step1 = cloneDeep(step1)
    },

    /** 調査情報(step2: 隣接建築物・周辺地盤及び構造躯体)更新 */
    setStep2Neighboring<T extends ConstructionType>(
      neighboring: AssessmentDetailStep2NeighboringStateTypes<T>
    ) {
      const constructionType = this.assessmentInfo.constructionType
      switch (constructionType) {
        case CONSTRUCTION_TYPE.WOOD:
          this.step2NeighboringWood = cloneDeep(
            neighboring as Step2Types.AssessmentDetailWoodStep2Neighboring
          )
          break
        case CONSTRUCTION_TYPE.STEEL:
          this.step2NeighboringSteel = cloneDeep(
            neighboring as Step2Types.AssessmentDetailSteelStep2Neighboring
          )
          break
        case CONSTRUCTION_TYPE.RC:
          this.step2NeighboringRc = cloneDeep(
            neighboring as Step2Types.AssessmentDetailRcStep2Neighboring
          )
          break
      }
    },

    /** 調査情報(step2: 落下危険物・転倒危険物)更新 */
    setStep2Falling<T extends ConstructionType>(
      falling: AssessmentDetailStep2FallingStateTypes<T>
    ) {
      const constructionType = this.assessmentInfo.constructionType
      switch (constructionType) {
        case CONSTRUCTION_TYPE.WOOD:
          this.step2FallingWood = cloneDeep(falling as Step2Types.AssessmentDetailWoodStep2Falling)
          break
        case CONSTRUCTION_TYPE.STEEL:
          this.step2FallingSteel = cloneDeep(
            falling as Step2Types.AssessmentDetailSteelStep2Falling
          )
          break
        case CONSTRUCTION_TYPE.RC:
          this.step2FallingRc = cloneDeep(falling as Step2Types.AssessmentDetailRcStep2Falling)
          break
      }
    },

    /** 応急総合判定更新 */
    setEmergencyAssessment(emergencyAssessment: EmergencyAssessment) {
      Object.assign(this.emergencyAssessment, emergencyAssessment)
    },

    /** 調査実施結果APIのupsert実行（オフラインの場合はIndexedDBに保存） */
    async apiUpdateInspectionResult(): Promise<void> {
      const param = buildInspectionResultUpsertParam<typeof this.assessmentInfo.constructionType>({
        assessmentDetail: {
          assessmentDetails: {
            step1: this.step1,
            step2: {
              neighboring:
                this.assessmentInfo.constructionType === CONSTRUCTION_TYPE.WOOD
                  ? this.step2NeighboringWood
                  : this.assessmentInfo.constructionType === CONSTRUCTION_TYPE.STEEL
                    ? this.step2NeighboringSteel
                    : this.step2NeighboringRc,
              falling:
                this.assessmentInfo.constructionType === CONSTRUCTION_TYPE.WOOD
                  ? this.step2FallingWood
                  : this.assessmentInfo.constructionType === CONSTRUCTION_TYPE.STEEL
                    ? this.step2FallingSteel
                    : this.step2FallingRc
            }
          } as AssessmentDetailMap[typeof this.assessmentInfo.constructionType]
        },
        assessmentId: this.commonResultSummary.assessmentId,
        emergencyAssessment: this.emergencyAssessment,
        assessmentInfo: this.assessmentInfo,
        building: this.building,
        buildingOverview: this.buildingOverview
      })

      if (param == null) {
        throw new GlobalError([ERROR_CODES.RESULT_PROPERTY_INVALID])
      }
      const networkStore = useNetworkStore()
      await accessDataBasedOnNetwork(
        'upsertInspectionResultApi',
        param,
        networkStore.getIsOnline,
        // オフラインの場合は、オフラインモードに移行する
        () => {
          networkStore.setOfflineStatus()
        }
      )
    },

    /**
     * 選択している家屋情報をstoreに反映
     * @param なし
     * @returns なし
     */
    async setIDBData(assessmentId: string) {
      const currentInspection: ResultSummary = (await inspectionResultGet({
        assessmentId: assessmentId
      })) as ResultSummary

      if (!currentInspection) {
        throw new GlobalError([ERROR_CODES.HOME_TARGET_INSPECTION_NOT_FOUND])
      }

      const constructionType = currentInspection.assessmentInfo.constructionType!
      this.setCommonResultSummary({
        assessmentId: currentInspection.assessmentId,
        insertId: currentInspection.insertId,
        insertDate: currentInspection.insertDate,
        updateId: currentInspection.updateId,
        updateDate: currentInspection.updateDate
      })
      this.setAssessmentInfo(
        currentInspection.assessmentInfo.serialNumber!,
        currentInspection.assessmentInfo.inspectedDatetime!,
        currentInspection.assessmentInfo.inspectionCount!,
        currentInspection.assessmentInfo.inspectorList!
      )
      this.setConstructionType(constructionType)
      this.setBuilding(currentInspection.building)
      this.setBuildingOverview(currentInspection.buildingOverview)
      this.setStep1(currentInspection.assessmentDetail.assessmentDetails.step1)
      this.setStep2Neighboring<typeof constructionType>(
        currentInspection.assessmentDetail.assessmentDetails.step2.neighboring
      )
      this.setStep2Falling<typeof constructionType>(
        currentInspection.assessmentDetail.assessmentDetails.step2.falling
      )
      this.setEmergencyAssessment(currentInspection.emergencyAssessment)

      // 調査破棄時の編集前後の差分確認のため、DB・APIから取得した撮影画像データを保持しておく
      this.lastFetchedImageData = this.getAllImagePathList()
    },

    /** 全調査項目の撮影画像リストを取得する */
    getAllImagePathList(): InspectionImageData[] {
      const targetInspectionItems = [
        this.step1.exterior,
        ...Object.values(this.step2NeighboringWood),
        ...Object.values(this.step2NeighboringSteel),
        ...Object.values(this.step2NeighboringRc),
        ...Object.values(this.step2FallingWood),
        ...Object.values(this.step2FallingSteel),
        ...Object.values(this.step2FallingRc),
        this.emergencyAssessment
      ] as const

      return targetInspectionItems.flatMap(e => {
        if (typeof e === 'string') {
          // 一部の調査項目は撮影画像が無いため除外する
          return []
        }
        // 撮影画像に関するデータのみをオブジェクトにまとめて返却
        return { imageDirectory: e.imageDirectory, imageFilePathList: [...e.imageFilePathList] }
      })
    },

    /** 全てのstateを初期化 */
    resetAll() {
      this.commonResultSummary = cloneDeep(commonResultSummary)
      this.assessmentInfo = cloneDeep(assessmentInfo)
      this.buildingOverview = cloneDeep(buildingOverview)
      this.building = cloneDeep(building)
      this.step1 = cloneDeep(step1)
      this.step2NeighboringWood = cloneDeep(step2NeighboringWood)
      this.step2NeighboringSteel = cloneDeep(step2NeighboringSteel)
      this.step2NeighboringRc = cloneDeep(step2NeighboringRc)
      this.step2FallingWood = cloneDeep(step2FallingWood)
      this.step2FallingSteel = cloneDeep(step2FallingSteel)
      this.step2FallingRc = cloneDeep(step2FallingRc)
      this.emergencyAssessment = cloneDeep(emergencyAssessment)
    },

    /** 共通データ以外のstateを初期化 */
    resetExceptCommonData() {
      this.assessmentInfo = cloneDeep(assessmentInfo)
      this.buildingOverview = cloneDeep(buildingOverview)
      this.building = cloneDeep(building)
      this.step1 = cloneDeep(step1)
      this.step2NeighboringWood = cloneDeep(step2NeighboringWood)
      this.step2NeighboringSteel = cloneDeep(step2NeighboringSteel)
      this.step2NeighboringRc = cloneDeep(step2NeighboringRc)
      this.step2FallingWood = cloneDeep(step2FallingWood)
      this.step2FallingSteel = cloneDeep(step2FallingSteel)
      this.step2FallingRc = cloneDeep(step2FallingRc)
      this.emergencyAssessment = cloneDeep(emergencyAssessment)
    }
  }
})
